import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useParams } from "react-router";
import { NumberUtils } from "utilities/number-utils";
import { useGetUserDashboard } from "utilities/hooks/use-get-user-dashboard";
import { RoleType } from "models/enumerations/users/role-type";
import { useNavigate } from "utilities/hooks/navigation/use-navigate";
import { UserLoginRecord } from "models/view-models/users/user-login-record";
import { UpdateUserLoginPathParams, UserLoginService } from "utilities/services/users/user-login-service";
import { useIdentity } from "utilities/hooks/use-identity";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization/t";
import { RouteUtils } from "utilities/route-utils";
import { sitemap } from "sitemap";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface SwitchProfilePageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const SwitchProfilePage: React.FC<SwitchProfilePageProps> = ((): JSX.Element => {
    const { roleType: roleTypeParam } = useParams();
    const { roleId: roleIdParam } = useParams();
    const roleType = useMemo(() => {
        if (NumberUtils.parseInt(roleTypeParam) == null) return null;
        return NumberUtils.parseInt(roleTypeParam) as RoleType;
    }, [roleTypeParam]);

    const roleId = useMemo(() => {
        if (NumberUtils.parseInt(roleIdParam) == null) return null;
        return NumberUtils.parseInt(roleIdParam) as number;
    }, [roleIdParam]);

    const { record: globalState, setRecord: setGlobalState } = useGlobalState();
    const { update: updateUserLoginApi } = UserLoginService.useUpdate();
    const { getIdentity } = useIdentity();
    const navigate = useNavigate();

    const [dashboardNavigationStarted, setDashboardNavigationStarted] = useState(false);

    const { dashboardPath } = useGetUserDashboard({
        userRole: globalState.currentIdentity?.role?.roleType,
        language: globalState.currentIdentity?.user?.preferredLanguage,
    });

    useEffect(() => {
        if (dashboardNavigationStarted) return;
        if (globalState.currentIdentity?.roleType() === roleType) {
            setDashboardNavigationStarted(true);
            navigate(dashboardPath);
        }
    }, [dashboardPath, navigate, globalState.currentIdentity, roleType]);

    const handleSwitchRole = useCallback(async () => { 
            if (!globalState.isAuthenticated() || !globalState.currentIdentity?.hasUserLoginId()) {
                return;
            }
            if (roleId == null) {
                return;
            }

            try {
                const pathParams: UpdateUserLoginPathParams = {
                    id: globalState.currentIdentity?.userLoginId(),
                };

                const updatedLogin: UserLoginRecord = globalState.currentIdentity!.userLogin!.with({
                    roleId: roleId,
                });

                const response = await updateUserLoginApi(updatedLogin, pathParams);
                const result = response?.result;

                if (result?.resultObject == null || result.hasErrors()) {
                    throw new Error();
                }

                try {
                    const identity = await getIdentity(result.resultObject);
                    setGlobalState((currentState) => currentState.setIdentity(identity));
                    ToastManager.success(
                        t("youAreLoggedInWithYourIdentityRoleNameRole", {
                            identityRoleName: identity?.role?.name,
                        })
                    );
                } catch {
                    ToastManager.error(t("thereWasAnIssueLoadingUserInfo"));
                }
            } catch {
                ToastManager.error(t("thereWasAnIssueSwitchingProfiles"));
                navigate(RouteUtils.localizePath(sitemap.public.noAccess));
            }
     },[]);


    useEffect(() => {
        if (globalState.currentIdentity?.roleType() !== roleType) {
            handleSwitchRole();
        }
    }, [globalState.currentIdentity, roleType]);

    return <></>;
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { SwitchProfilePage };

// #endregion Exports
